import React, { useEffect, useState, useRef } from "react"; // Import useRef here
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import Header, {
	NavLink,
	LogoLink,
	NavToggle,
	DesktopNavLinks,
} from "../components/headers/light.js";
import icon from "../images/lra.svg";
import { PrimaryButton } from "../components/misc/Buttons.js";
import { useParams } from "react-router-dom";
import supabase from "helpers/SupabaseClient";
import Watermark from "helpers/Watermark.js";
import { watermarkImageWithText } from "./Watermark";
import { useAuth } from "./Auth.js";
import PdfViewer from "helpers/PdfViewer.js";

const HeadingRow = tw.div`flex justify-between items-center sm:flex-col md:flex-row`;
const Heading = tw.h2`text-2xl sm:text-4xl font-black tracking-wide text-center text-gray-900 mb-0 sm:mb-10 mb-5`;

const LangButtonContainer = tw.div`flex justify-end w-full`;
const LangButton = tw(PrimaryButton)`m-2 mt-4 w-full sm:w-32`;
const StyledHeader = styled(Header)`
	${tw`pt-0 max-w-none w-full`}
	${DesktopNavLinks} ${NavLink}, ${LogoLink} {
		${tw`text-black hover:border-gray-300 hover:text-gray-300`}
	}
	${NavToggle}.closed {
		${tw`text-gray-100 hover:text-primary-500`}
	}
`;
const HighlightedText = tw.span`bg-primary-500 text-gray-100 px-4 transform -skew-x-12 inline-block`;

const VideoContainer = styled.div`
	width: 100%;
	height: 0;
	padding-bottom: 56.25%;
	position: relative;
	overflow: hidden;

	@media (max-width: 768px) {
		padding-bottom: 55%;
	}

	.swarm-fluid {
		width: 100%;
		height: 100%;
		object-fit: contain; /* Or cover, depending on your preference */
	}
`;

const Text = styled.div`
	${tw`text-lg  text-gray-800`}
	p {
		${tw`mt-2 leading-loose`}
	}
	h1 {
		${tw`text-3xl font-bold mt-10`}
	}
	h2 {
		${tw`text-2xl font-bold mt-8`}
	}
	h3 {
		${tw`text-xl font-bold mt-6`}
	}
	ul {
		${tw`list-disc list-inside`}
		li {
			${tw`ml-2 mb-3`}
			p {
				${tw`mt-0 inline leading-normal`}
			}
		}
	}
`;

export default () => {
	const { user } = useAuth();
	const [userId, setUserId] = useState("Interesting");
	const videoRef = useRef(null);

	const { url } = useParams();
	const [vid_key, setVidKey] = useState(url.split("-->")[0]);
	const [pdf, setPdf] = useState(null);
	const title = url.split("-->")[1];
	const vid_id = parseInt(url.split("-->")[2] ?? null);
	const [videoOptions, setVideoOptions] = useState([]);
	const origLang = {
		language: "English",
		video_key: url.split("-->")[0],
	};

	useEffect(() => {
		// Disable fullscreen button (and potentially other controls)
		const videoElement = videoRef.current;
		if (videoElement) {
			videoElement.controlsList = "nodownload"; // Remove download button

			// Attempt to remove the fullscreen button (may not work in all browsers)
			const fullscreenButton = videoElement.querySelector(
				".vjs-fullscreen-control"
			);
			if (fullscreenButton) {
				fullscreenButton.style.display = "none";
			}

			// Detect and prevent fullscreen attempts via API
			videoElement.addEventListener("webkitendfullscreen", () => {
				if (document.webkitIsFullScreen) {
					document.webkitExitFullscreen();
				}
			});
		}
	}, []);

	useEffect(() => {
		const interval = setInterval(() => {
			const player = document.querySelector('.vjs-has-started');
			console.dir("player: ", player)

			if (player) {
				console.log("Swarmify Player is ready");

				// Prevent duplicate injections
				if (!document.querySelector(".vjs-rewind-button")) {
					const rewindButton = document.createElement("button");
					rewindButton.className = "vjs-control vjs-rewind-button";
					rewindButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#ccc" stroke="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-rewind-icon lucide-rewind"><path d="M12 6a2 2 0 0 0-3.414-1.414l-6 6a2 2 0 0 0 0 2.828l6 6A2 2 0 0 0 12 18z"/><path d="M22 6a2 2 0 0 0-3.414-1.414l-6 6a2 2 0 0 0 0 2.828l6 6A2 2 0 0 0 22 18z"/></svg>`;
					rewindButton.onclick = () => {
						const video = player.querySelector("video");
						video.currentTime -= 10;
					};
					player.querySelector(".vjs-control-bar")?.appendChild(rewindButton);
				}

				if (!document.querySelector(".vjs-forward-button")) {
					const forwardButton = document.createElement("button");
					forwardButton.className = "vjs-control vjs-button vjs-forward-button";
					forwardButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#ccc" stroke="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-fast-forward-icon lucide-fast-forward"><path d="M12 6a2 2 0 0 1 3.414-1.414l6 6a2 2 0 0 1 0 2.828l-6 6A2 2 0 0 1 12 18z"/><path d="M2 6a2 2 0 0 1 3.414-1.414l6 6a2 2 0 0 1 0 2.828l-6 6A2 2 0 0 1 2 18z"/></svg>`;
					forwardButton.onclick = () => {
						const video = player.querySelector("video");
						video.currentTime += 10;
					};
					player.querySelector(".vjs-control-bar")?.appendChild(forwardButton);
				}

				clearInterval(interval); // Done
			}
		}, 500); // check every 500ms

		return () => clearInterval(interval);
	}, [vid_id]);


	useEffect(() => {
		async function fetchLangs() {
			if (vid_id) {
				try {
					// Correctly destructure the response to get 'data' and 'error'
					let { data, error } = await supabase
						.from("video_langs")
						.select("*")
						.eq("service", vid_id);

					// Log the data to ensure it is returned correctly
					console.log("Fetched data:", data);

					if (error) {
						console.error("Error fetching languages:", error);
						return; // Exit early if there is an error
					}

					// Check if 'data' is defined and not empty before updating state
					if (data && Array.isArray(data)) {
						data = data.filter((v) => v.video_key !== vid_key);
						console.log(
							`Adding English: [${
								origLang.video_key
							} != ${vid_key}] ${origLang.video_key !== vid_key}`
						);
						if (origLang.video_key !== vid_key) {
							console.log("Adding English");
							setVideoOptions([...data, origLang]);
							return;
						}
						setVideoOptions(data);
						return;
					} else {
						console.log("No data found for languages");
					}
				} catch (error) {
					console.error(
						"An error occurred while fetching languages:",
						error
					);
				}
				return null;
			}
		}

		async function setUser() {
			try {
				const { data, error } = await supabase
					.from("access_report")
					.select("*")
					.eq("email", user.email);

				console.log(data[0]);
				setUserId((prevUserId) => data[0].registration_number);
				return data[0].registration_number;
			} catch (error) {
				console.error(error);
			}
		}

		async function getPdf() {
			try {
				const { data, error } = await supabase
					.from("service")
					.select("file")
					.eq("id", vid_id);

				console.log(data[0]);
				setPdf(data[0].file);
			} catch (error) {
				console.error(error);
			}
		}

		const setWatermarkWidth = async () => {
			const userID = await setUser();
			console.log(userID);
			const langs = await fetchLangs();
			console.log(langs);
			const pdf = await getPdf();
			console.log(pdf);
			try {
				// This function will be called when the entire page (including images and stylesheets) is fully loaded
				const elements =
					document.getElementsByClassName("vjs-watermark");
				// Check if elements exist
				if (elements.length > 0) {
					// Access the first element (assuming there's only one)
					console.log(elements);
					const watermark = elements[0];
					if (userId !== "") {
						watermark.setAttribute(
							"src",
							watermarkImageWithText(userID)
						);
						watermark.setAttribute(
							"style",
							"min-width: 80% !important; pointer-events: none;"
						);
					}
				}
			} catch (error) {
				console.log("Error in setWatermarkWidth:", error);
			}
		};

		console.log(`vid ID: ${vid_id}`);
		const timeoutId = setTimeout(setWatermarkWidth, 2000);
		return () => clearTimeout(timeoutId);
	}, [user.email, vid_key]);

	const handleLangChange = (videoLang) => {
		const vid = videoOptions.filter((v) => v.language === videoLang)[0];
		console.log(vid.video_key);
		setVidKey(vid.video_key);
	};

	var file_url;
	if (pdf != null) {
		file_url = `https://zegleedmcpswaxynxnta.supabase.co/storage/v1/object/public/assets/${pdf}.pdf`;
	}

	return (
		<AnimationRevealPage>
			<StyledHeader links={[]} logoIcon={icon} />
			<Container>
				<ContentWithPaddingXl>
					<HeadingRow>
						<Heading>
							{
								<>
									<HighlightedText>{title}</HighlightedText>
								</>
							}
						</Heading>
					</HeadingRow>
					<VideoContainer key={vid_key}>
						<smartvideo
							ref={videoRef} // Add this ref
							className="swarm-fluid"
							controls
							playsInline
							webkit-playsinline="true"
							src={
								vid_key.length === 11
									? `https://www.youtube.com/embed/${vid_key}?playsinline=1`
									: `swarmify://${vid_key}`
							}
						></smartvideo>
					</VideoContainer>
					{vid_id != null && (
						<>
							<LangButtonContainer>
								{videoOptions.map((lang, index) => (
									<LangButton
										key={index}
										onClick={() =>
											handleLangChange(lang.language)
										}
									>
										{lang.language}
									</LangButton>
								))}
							</LangButtonContainer>
						</>
					)}

					<br />
					<br />
					<Text>
						<Watermark></Watermark>
						{pdf != null ? (
							<PdfViewer
								pdf={{
									file: file_url,
								}}
							/>
						) : (
							""
						)}
					</Text>
				</ContentWithPaddingXl>
			</Container>
		</AnimationRevealPage>
	);
};
